import { useState } from 'react';
import { Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
// Components
import SocialMediaButton from '../SocialMediaButton/SocialMediaButton';
import NodeOffer from './NodeOffer';
// Hooks
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useMarketingFunnel } from '@/hooks/useMarketingFunnel';
import { useFeatureFlags } from '@/providers/FeatureFlagProvider';
// Utils
import { socialMedia, modalFallback } from './constants';

export default function ShareModal({
  closeModal,
  currentLink,
  offer,
  cmsData,
}): JSX.Element {
  const { memberData } = useAuth();
  const { featureFlags }: any = useFeatureFlags();
  const { handleSetEngageTag } = useMarketingFunnel();

  const [isCopied, setIsCopied] = useState(false);
  const [supported, setSupported] = useState(true);
  const hasQuery = currentLink?.includes('?');
  const shareLink = `${currentLink}${hasQuery ? '&' : '?'}sponsor=${
    memberData?.memberNumber
  }`;
  const isMobile = useIsMobile(520);

  const handleCopyClick = () => {
    handleSetEngageTag(['share-link-active']);
    navigator.clipboard
      .writeText(shareLink)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 1600);
      })
      .catch((error) => {
        console.error(error);
      });
    if (isMobile) {
      if (navigator.share) {
        navigator
          .share({
            title: 'Join me today',
            url: shareLink,
          })
          .catch(() => {
            setSupported(false);
          });
      } else {
        setSupported(false);
      }
    }
  };

  const header = offer
    ? cmsData?.title ?? modalFallback.title
    : cmsData?.altTitle ?? modalFallback.altTitle;

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <IconButton
        sx={{
          position: 'absolute',
          right: -20,
          top: -24,
        }}
        onClick={closeModal}
        data-core-qa="closeModal"
      >
        <CloseIcon
          sx={{
            width: '20px',
          }}
        />
      </IconButton>
      <Box sx={{ marginBottom: '16px', maxWidth: '93%' }}>
        <Typography variant="h4">{header}</Typography>
      </Box>
      {featureFlags['Rewards.Offers.Show'] ? (
        <NodeOffer items={cmsData?.listItem} subTitle={cmsData?.subTitle} />
      ) : (
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="body1">
            {cmsData?.altSubTitle ?? modalFallback.altSubTitle}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          '@media (max-width: 520px)': {
            flexDirection: 'column',
          },
        }}
      >
        <TextField
          value={shareLink}
          size="small"
          sx={{ flexGrow: '1', maxWidth: '500px' }}
        />
        <Tooltip
          title={
            isCopied
              ? cmsData?.primaryButton?.helpText ?? modalFallback.primaryButton.helpText
              : ''
          }
        >
          <Button
            variant="contained"
            onClick={handleCopyClick}
            data-core-qa="copyButton"
            fullWidth={isMobile}
          >
            {isMobile
              ? 'Share Link'
              : cmsData?.primaryButton?.title ?? modalFallback.primaryButton.title}
          </Button>
        </Tooltip>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: isMobile ? 'center' : 'flex-start',
          gap: '24px',
          marginTop: '24px',
        }}
      >
        {socialMedia(shareLink).map((social) => (
          <SocialMediaButton
            key={social.dataTag}
            shareLink={shareLink}
            socialIcon={social}
            supported={supported}
            setSupported={setSupported}
          />
        ))}
      </Box>
    </Box>
  );
}
